import React, { useState, useEffect, useMemo } from 'react';
import {
    Plus, Search, Loader2, Trash2, Pencil, Zap, History, Phone, Mail, MessageCircle,
    ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Download,
    Target, AlarmClock, CalendarCheck, Trophy, Briefcase, X, Flame, BarChart3,
    Upload, FileSpreadsheet, CheckCircle2, XCircle, Clock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Drawer from '../components/Drawer';
import ProspectForm from '../components/ProspectForm';
import ProspectActivity from '../components/ProspectActivity';
import ProspectHistory from '../components/ProspectHistory';
import ProspectImportModal from '../components/ProspectImportModal';
import ProspectExportModal from '../components/ProspectExportModal';
import { getProspects, updateProspect, deleteProspect } from '../services/prospectService';
import {
    STATUTS, STATUT_MAP, STATUTS_CLOS, SOURCES, SOURCE_MAP,
    interetLevel, formatDate, daysFromToday, echeanceState, isWaitingAction,
    hadActionToday, hasActionHistory
} from '../config/prospects';
import { exportProspectsToExcel } from '../utils/excelProspects';

const ITEMS_PER_PAGE = 25;
const STATUT_ORDER = Object.fromEntries(STATUTS.map((s, i) => [s.value, i]));

const QUICK_VIEWS = [
    { id: 'all', label: 'Tous' },
    { id: 'today_actions', label: '⚡ Traités aujourd\'hui' },
    { id: 'with_action', label: '💬 Avec échange' },
    { id: 'todo', label: 'À traiter' },
    { id: 'week', label: 'Cette semaine' },
    { id: 'active', label: 'En cours' },
    { id: 'won', label: 'Gagnés' },
    { id: 'lost', label: 'Perdus' }
];

// ── Helpers ──
const whatsappLink = (phone) => {
    let digits = (phone || '').replace(/\D/g, '');
    if (!digits) return null;
    if (digits.length === 8) digits = `216${digits}`; // Tunisian local number
    if (digits.startsWith('00')) digits = digits.slice(2);
    return `https://wa.me/${digits}`;
};

// ── Small UI pieces ──
const StatutBadge = ({ statut }) => {
    const s = STATUT_MAP[statut] || STATUTS[0];
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-black uppercase tracking-wider border rounded whitespace-nowrap ${s.badge}`}>
            <span className="text-[11px] leading-none">{s.emoji}</span>
            {s.label}
        </span>
    );
};

const InteretBar = ({ value = 0 }) => {
    const lvl = interetLevel(value);
    return (
        <div className="w-28 flex flex-col gap-1 select-none" title={`${value}% · ${lvl.label}`}>
            <div className="flex items-center justify-between gap-1 leading-none">
                <span className={`text-xs font-black font-mono ${lvl.text}`}>{value}%</span>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tight whitespace-nowrap">
                    {lvl.label}
                </span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-300 ${lvl.bar}`}
                    style={{ width: `${Math.max(4, Math.min(100, value))}%` }}
                />
            </div>
        </div>
    );
};

const EcheanceBadge = ({ prospect }) => {
    if (prospect.statut === 'gagne') {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200 whitespace-nowrap">
                <CheckCircle2 size={11} className="text-emerald-600 shrink-0" /> Dossier clos
            </span>
        );
    }
    if (prospect.statut === 'pas_interesse') {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200 whitespace-nowrap">
                <XCircle size={11} className="text-slate-400 shrink-0" /> Archivé
            </span>
        );
    }
    if (!prospect.dateProchaineAction) {
        if (isWaitingAction(prospect.prochaineAction)) {
            return (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-1 rounded whitespace-nowrap">
                    <Clock size={11} className="text-indigo-500 shrink-0" /> En attente de retour
                </span>
            );
        }
        return <span className="text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded whitespace-nowrap">À planifier</span>;
    }
    const state = echeanceState(prospect);
    const diff = daysFromToday(prospect.dateProchaineAction);
    const baseDate = formatDate(prospect.dateProchaineAction);
    const timeStr = prospect.heureProchaineAction ? ` ${prospect.heureProchaineAction}` : '';
    const date = `${baseDate}${timeStr}`;

    if (state === 'late') {
        return (
            <div className="inline-flex flex-col">
                <span className="text-[10px] font-black text-white bg-red-500 px-2 py-1 rounded whitespace-nowrap">{date} · Retard {Math.abs(diff)}j</span>
            </div>
        );
    }
    if (state === 'today') return <span className="text-[10px] font-black text-white bg-orange-500 px-2 py-1 rounded whitespace-nowrap">{date} · Aujourd'hui</span>;
    if (state === 'soon') return <span className="text-[10px] font-black text-blue-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded whitespace-nowrap">{date} · {diff === 1 ? 'Demain' : `J-${diff}`}</span>;
    return <span className="text-xs font-bold text-slate-600 font-mono whitespace-nowrap">{date}</span>;
};

const ProchaineActionBadge = ({ prospect }) => {
    if (prospect.statut === 'gagne') {
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 whitespace-nowrap">
                <CheckCircle2 size={12} className="text-emerald-600 shrink-0" /> Client converti
            </span>
        );
    }
    if (prospect.statut === 'pas_interesse') {
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-bold text-slate-500 bg-slate-100 border border-slate-200 whitespace-nowrap">
                <XCircle size={12} className="text-slate-400 shrink-0" /> Sans suite (Refus)
            </span>
        );
    }
    if (isWaitingAction(prospect.prochaineAction)) {
        return (
            <div className="text-xs font-bold text-indigo-700 truncate flex items-center gap-1" title={prospect.prochaineAction}>
                <span>⏳</span>
                <span>{prospect.prochaineAction}</span>
            </div>
        );
    }
    return (
        <div className="text-xs font-bold text-blue-700 truncate" title={prospect.prochaineAction}>
            {prospect.prochaineAction || <span className="text-slate-300 font-normal">—</span>}
        </div>
    );
};

const Kpi = ({ icon, label, value, sub, tone = 'slate', active, onClick }) => {
    const tones = {
        slate: 'text-slate-600 bg-slate-100',
        red: 'text-red-600 bg-red-50',
        indigo: 'text-indigo-600 bg-indigo-50',
        blue: 'text-blue-600 bg-blue-50',
        green: 'text-green-600 bg-green-50'
    };
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left p-4 bg-white border rounded-xl transition-all hover:shadow-sm ${active ? 'border-slate-900 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300'}`}
        >
            <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</span>
                <span className={`p-1.5 rounded-lg ${tones[tone]}`}>{icon}</span>
            </div>
            <div className="text-2xl font-black text-slate-900 tracking-tight">{value}</div>
            {sub && <div className="text-[10px] font-bold text-slate-400 mt-1">{sub}</div>}
        </button>
    );
};

const SortHeader = ({ label, sortKey, sort, onSort, className = '' }) => {
    const active = sort.key === sortKey;
    const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
    return (
        <th className={`px-4 py-3.5 border-b border-slate-200 text-[10px] font-black uppercase tracking-wider whitespace-nowrap bg-slate-50 select-none ${active ? 'text-blue-600' : 'text-slate-500'} ${className}`}>
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                className="inline-flex items-center gap-1.5 hover:text-slate-900 transition"
            >
                <span>{label}</span>
                <Icon size={11} className={active ? 'text-blue-600' : 'text-slate-300'} />
            </button>
        </th>
    );
};

const Th = ({ children, className = '' }) => (
    <th className={`px-4 py-3.5 border-b border-slate-200 text-[10px] font-black text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 select-none ${className}`}>
        {children}
    </th>
);

// ── Page ──
const Prospects = () => {
    const [prospects, setProspects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [search, setSearch] = useState('');
    const [statutFilter, setStatutFilter] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [responsableFilter, setResponsableFilter] = useState('');
    const [quickView, setQuickView] = useState('all');
    const [sort, setSort] = useState({ key: 'dateProchaineAction', dir: 'asc' });
    const [page, setPage] = useState(1);
    const [showSources, setShowSources] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const clearSelection = () => setSelectedIds(new Set());

    // drawer: { mode: 'create' | 'edit' | 'activity', id?: string }
    const [drawer, setDrawer] = useState(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [updatingStatutId, setUpdatingStatutId] = useState(null);
    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type, id: Date.now() });
    };

    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(() => setToast(null), 2800);
        return () => clearTimeout(t);
    }, [toast]);

    const fetchData = async () => {
        try {
            const data = await getProspects();
            setProspects(Array.isArray(data) ? data : []);
            setLoadError('');
        } catch (err) {
            console.error('Error fetching prospects:', err);
            setLoadError(err.response?.data?.message || 'Impossible de charger les prospects.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const upsertLocal = (p) => {
        setProspects(prev => {
            const exists = prev.some(x => x._id === p._id);
            return exists ? prev.map(x => (x._id === p._id ? p : x)) : [p, ...prev];
        });
    };

    const responsables = useMemo(
        () => Array.from(new Set(prospects.map(p => p.responsable).filter(Boolean))).sort(),
        [prospects]
    );

    // ── Stats ──
    const stats = useMemo(() => {
        const total = prospects.length;
        let late = 0, today = 0, demos = 0, active = 0, won = 0, week = 0;
        const byStatut = {};
        const bySource = {};
        for (const p of prospects) {
            byStatut[p.statut] = (byStatut[p.statut] || 0) + 1;
            const src = p.source || 'autre';
            bySource[src] = bySource[src] || { total: 0, won: 0, active: 0 };
            bySource[src].total++;
            const st = echeanceState(p);
            if (st === 'late') late++;
            if (st === 'today') today++;
            if (st === 'today' || st === 'soon') week++;
            if (p.statut === 'demo_programmee') demos++;
            if (p.statut === 'gagne') { won++; bySource[src].won++; }
            if (!STATUTS_CLOS.includes(p.statut)) { active++; bySource[src].active++; }
        }
        const conversion = total ? Math.round((won / total) * 100) : 0;
        return { total, late, today, demos, active, won, week, conversion, byStatut, bySource };
    }, [prospects]);

    // ── Filtering + sorting ──
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = prospects.filter(p => {
            if (q) {
                const hay = [p.etablissement, p.contact, p.telephone, p.email, p.ville, p.notes, p.objection, p.typeEtablissement]
                    .filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (statutFilter && p.statut !== statutFilter) return false;
            if (sourceFilter && p.source !== sourceFilter) return false;
            if (responsableFilter && p.responsable !== responsableFilter) return false;

            const st = echeanceState(p);
            switch (quickView) {
                case 'today_actions': return hadActionToday(p);
                case 'with_action': return hasActionHistory(p);
                case 'todo': return st === 'late' || st === 'today' || (!p.dateProchaineAction && !STATUTS_CLOS.includes(p.statut) && !isWaitingAction(p.prochaineAction));
                case 'week': return st === 'late' || st === 'today' || st === 'soon';
                case 'active': return !STATUTS_CLOS.includes(p.statut);
                case 'won': return p.statut === 'gagne';
                case 'lost': return p.statut === 'pas_interesse';
                default: return true;
            }
        });

        const dir = sort.dir === 'asc' ? 1 : -1;
        const val = (p) => {
            switch (sort.key) {
                case 'etablissement': return (p.etablissement || '').toLowerCase();
                case 'contact': return (p.contact || '').toLowerCase();
                case 'statut': return STATUT_ORDER[p.statut] ?? 99;
                case 'interet': return p.interet || 0;
                case 'source': return (SOURCE_MAP[p.source]?.label || '').toLowerCase();
                case 'dateDerniereAction': return p.dateDerniereAction ? new Date(p.dateDerniereAction).getTime() : null;
                case 'prochaineAction': return (p.prochaineAction || '').toLowerCase();
                case 'dateProchaineAction':
                    // closed prospects and missing dates always go last
                    if (STATUTS_CLOS.includes(p.statut)) return null;
                    return p.dateProchaineAction ? new Date(p.dateProchaineAction).getTime() : null;
                case 'responsable': return (p.responsable || '').toLowerCase();
                default: return 0;
            }
        };

        return [...list].sort((a, b) => {
            const va = val(a), vb = val(b);
            if (va === null && vb === null) return 0;
            if (va === null) return 1;
            if (vb === null) return -1;
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });
    }, [prospects, search, statutFilter, sourceFilter, responsableFilter, quickView, sort]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const isAllPageSelected = pageRows.length > 0 && pageRows.every(p => selectedIds.has(p._id));

    const toggleSelectPage = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (isAllPageSelected) {
                pageRows.forEach(p => next.delete(p._id));
            } else {
                pageRows.forEach(p => next.add(p._id));
            }
            return next;
        });
    };

    useEffect(() => { setPage(1); }, [search, statutFilter, sourceFilter, responsableFilter, quickView]);

    const hasFilters = search || statutFilter || sourceFilter || responsableFilter || quickView !== 'all';
    const resetFilters = () => {
        setSearch(''); setStatutFilter(''); setSourceFilter(''); setResponsableFilter(''); setQuickView('all');
    };

    const handleSort = (key) => {
        setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'interet' ? 'desc' : 'asc' });
    };

    // ── Actions ──
    const handleInlineStatut = async (p, statut) => {
        setUpdatingStatutId(p._id);
        try {
            const updated = await updateProspect(p._id, { statut });
            upsertLocal(updated);
            showToast(`Statut : ${STATUT_MAP[statut]?.label}`);
        } catch (err) {
            showToast(err.response?.data?.message || 'Erreur de mise à jour', 'error');
        } finally {
            setUpdatingStatutId(null);
        }
    };

    const handleDelete = async (id) => {
        setDeleteConfirmId(null);
        setDeletingId(id);
        try {
            await deleteProspect(id);
            setProspects(prev => prev.filter(p => p._id !== id));
            if (drawer?.id === id) setDrawer(null);
            showToast('Prospect supprimé');
        } catch (err) {
            showToast(err.response?.data?.message || 'Suppression impossible', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const drawerProspect = drawer?.id ? prospects.find(p => p._id === drawer.id) : null;

    return (
        <div className="flex-1 bg-white flex flex-col relative min-h-screen">
            {/* ── Toolbar ── */}
            <div className="bg-slate-50 border-b border-slate-200 px-8 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-white border border-slate-200 rounded-lg text-slate-600">
                        <Target size={16} />
                    </div>
                    <div>
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Suivi Commercial XoraLearn</span>
                        <span className="ml-3 text-[10px] font-bold text-slate-400 bg-slate-200/60 px-2 py-0.5 rounded-md font-mono">{prospects.length} Prospects</span>
                    </div>
                </div>
                <div className="flex items-center gap-2.5">
                    {selectedIds.size > 0 && (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-900 border border-emerald-200 rounded-lg text-xs font-bold animate-fadeIn">
                            <span>{selectedIds.size} sélectionné(s)</span>
                            <button
                                onClick={() => setIsExportModalOpen(true)}
                                className="px-2.5 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[11px] font-bold shadow-2xs transition"
                            >
                                Exporter
                            </button>
                            <button
                                onClick={clearSelection}
                                className="text-slate-400 hover:text-slate-700 ml-0.5 p-0.5 rounded hover:bg-emerald-100"
                                title="Désélectionner tout"
                            >
                                <X size={12} />
                            </button>
                        </div>
                    )}
                    <button
                        onClick={() => setIsImportModalOpen(true)}
                        className="inline-flex items-center gap-2 px-3.5 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200/80 rounded-lg text-xs font-bold transition shadow-2xs"
                        title="Importer des prospects depuis un fichier Excel (.xlsx, .xls) ou CSV"
                    >
                        <Upload size={14} className="text-emerald-600" />
                        <span>Importer Excel</span>
                    </button>
                    <button
                        onClick={() => setIsExportModalOpen(true)}
                        disabled={prospects.length === 0}
                        className="inline-flex items-center gap-2 px-3.5 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-lg text-xs font-bold transition disabled:opacity-40 shadow-2xs"
                        title="Choisir et exporter les prospects dans un classeur Excel (.xlsx)"
                    >
                        <FileSpreadsheet size={14} className="text-emerald-600" />
                        <span>Exporter Excel (.xlsx)</span>
                        {selectedIds.size > 0 && (
                            <span className="bg-emerald-600 text-white font-mono text-[10px] font-black px-1.5 py-0.2 rounded-full">
                                {selectedIds.size}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setDrawer({ mode: 'create' })}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 transition shadow-sm"
                    >
                        <Plus size={14} />
                        Nouveau Prospect
                    </button>
                </div>
            </div>

            <div className="px-8 pt-6 space-y-5">
                {/* ── KPIs ── */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <Kpi icon={<Briefcase size={14} />} label="Pipeline actif" value={stats.active} sub={`${stats.total} prospects au total`} active={quickView === 'active'} onClick={() => setQuickView(quickView === 'active' ? 'all' : 'active')} />
                    <Kpi icon={<AlarmClock size={14} />} tone="red" label="À traiter" value={stats.late + stats.today} sub={`${stats.late} en retard · ${stats.today} aujourd'hui`} active={quickView === 'todo'} onClick={() => setQuickView(quickView === 'todo' ? 'all' : 'todo')} />
                    <Kpi icon={<CalendarCheck size={14} />} tone="blue" label="Cette semaine" value={stats.week + stats.late} sub="Relances sur 7 jours" active={quickView === 'week'} onClick={() => setQuickView(quickView === 'week' ? 'all' : 'week')} />
                    <Kpi icon={<Flame size={14} />} tone="indigo" label="Démos programmées" value={stats.demos} sub="Statut « Démo programmée »" active={statutFilter === 'demo_programmee'} onClick={() => setStatutFilter(statutFilter === 'demo_programmee' ? '' : 'demo_programmee')} />
                    <Kpi icon={<Trophy size={14} />} tone="green" label="Clients gagnés" value={stats.won} sub={`Taux de conversion ${stats.conversion}%`} active={quickView === 'won'} onClick={() => setQuickView(quickView === 'won' ? 'all' : 'won')} />
                </div>

                {/* ── Pipeline by status ── */}
                <div className="flex flex-wrap gap-1.5">
                    {STATUTS.map(s => {
                        const count = stats.byStatut[s.value] || 0;
                        const active = statutFilter === s.value;
                        return (
                            <button
                                key={s.value}
                                onClick={() => setStatutFilter(active ? '' : s.value)}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-bold rounded-lg border transition ${active ? 'bg-slate-900 text-white border-slate-900' : count ? 'bg-white text-slate-700 border-slate-200 hover:border-slate-400' : 'bg-white text-slate-300 border-slate-100'}`}
                            >
                                <span>{s.emoji}</span>
                                {s.label}
                                <span className={`font-mono px-1.5 rounded ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>{count}</span>
                            </button>
                        );
                    })}
                </div>

                {/* ── Filters ── */}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[220px] max-w-sm">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Rechercher école, contact, téléphone, notes…"
                            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                        />
                    </div>

                    <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                        {QUICK_VIEWS.map(v => (
                            <button
                                key={v.id}
                                onClick={() => setQuickView(v.id)}
                                className={`px-3 py-1.5 text-[11px] font-bold rounded-md transition ${quickView === v.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>

                    <select
                        value={sourceFilter}
                        onChange={e => setSourceFilter(e.target.value)}
                        className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 outline-none focus:border-blue-500"
                    >
                        <option value="">Toutes les sources</option>
                        {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>

                    {responsables.length > 0 && (
                        <select
                            value={responsableFilter}
                            onChange={e => setResponsableFilter(e.target.value)}
                            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 outline-none focus:border-blue-500"
                        >
                            <option value="">Tous les responsables</option>
                            {responsables.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    )}

                    <button
                        onClick={() => setShowSources(v => !v)}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-xs font-bold transition ${showSources ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                    >
                        <BarChart3 size={13} />
                        Performance canaux
                    </button>

                    {hasFilters && (
                        <button onClick={resetFilters} className="inline-flex items-center gap-1 px-2.5 py-2 text-xs font-bold text-slate-500 hover:text-slate-900">
                            <X size={13} /> Réinitialiser
                        </button>
                    )}
                </div>

                {/* ── Source performance ── */}
                <AnimatePresence>
                    {showSources && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="p-4 border border-slate-200 rounded-xl bg-slate-50/50">
                                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">Quel canal ramène le plus de clients ?</div>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                                    {SOURCES
                                        .map(s => ({ ...s, ...(stats.bySource[s.value] || { total: 0, won: 0, active: 0 }) }))
                                        .sort((a, b) => b.won - a.won || b.total - a.total)
                                        .map(s => {
                                            const rate = s.total ? Math.round((s.won / s.total) * 100) : 0;
                                            return (
                                                <button
                                                    key={s.value}
                                                    onClick={() => setSourceFilter(sourceFilter === s.value ? '' : s.value)}
                                                    className={`text-left p-3 bg-white border rounded-lg transition ${sourceFilter === s.value ? 'border-slate-900' : 'border-slate-200 hover:border-slate-300'} ${s.total ? '' : 'opacity-50'}`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-bold text-slate-900">{s.label}</span>
                                                        <span className="text-[10px] font-mono font-bold text-slate-400">{s.total}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-2">
                                                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                            <div className="h-full bg-green-500 rounded-full" style={{ width: `${rate}%` }} />
                                                        </div>
                                                        <span className="text-[10px] font-black text-green-700 font-mono">{rate}%</span>
                                                    </div>
                                                    <div className="text-[10px] text-slate-400 font-bold mt-1">{s.won} gagné(s) · {s.active} en cours</div>
                                                </button>
                                            );
                                        })}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* ── Table ── */}
            <div className="flex-1 mt-5 overflow-x-auto border-t border-slate-200">
                <table className="w-full min-w-[1720px] text-left border-separate border-spacing-0">
                    <thead>
                        <tr className="bg-slate-50">
                            {/* 1. Client / Établissement (Sticky Left avec Checkbox) */}
                            <th className="sticky left-0 z-30 min-w-[250px] max-w-[280px] pl-5 pr-4 py-3.5 shadow-[3px_0_6px_-2px_rgba(0,0,0,0.06)] border-r border-slate-200 bg-slate-50 border-b select-none">
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        checked={isAllPageSelected}
                                        onChange={toggleSelectPage}
                                        className="w-4 h-4 rounded text-emerald-600 border-slate-300 focus:ring-emerald-500 cursor-pointer shrink-0"
                                        title={isAllPageSelected ? "Tout désélectionner sur cette page" : "Tout sélectionner sur cette page"}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleSort('etablissement')}
                                        className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-slate-700 hover:text-slate-900 transition"
                                    >
                                        <span>Client / Établissement</span>
                                        {sort.key === 'etablissement' ? (
                                            sort.dir === 'asc' ? <ArrowUp size={11} className="text-blue-600" /> : <ArrowDown size={11} className="text-blue-600" />
                                        ) : (
                                            <ArrowUpDown size={11} className="text-slate-300" />
                                        )}
                                    </button>
                                </div>
                            </th>

                            {/* 2. Contact */}
                            <SortHeader
                                label="Contact"
                                sortKey="contact"
                                sort={sort}
                                onSort={handleSort}
                                className="min-w-[140px] max-w-[160px]"
                            />

                            {/* 3. Téléphone */}
                            <Th className="min-w-[140px]">Téléphone</Th>

                            {/* 4. Email */}
                            <Th className="min-w-[180px] max-w-[200px]">Email</Th>

                            {/* 5. Source */}
                            <SortHeader
                                label="Source"
                                sortKey="source"
                                sort={sort}
                                onSort={handleSort}
                                className="min-w-[110px]"
                            />

                            {/* 6. Statut */}
                            <SortHeader
                                label="Statut"
                                sortKey="statut"
                                sort={sort}
                                onSort={handleSort}
                                className="min-w-[175px]"
                            />

                            {/* 7. Intérêt */}
                            <SortHeader
                                label="Intérêt"
                                sortKey="interet"
                                sort={sort}
                                onSort={handleSort}
                                className="min-w-[140px]"
                            />

                            {/* 8. Dernière action */}
                            <SortHeader
                                label="Dernière action"
                                sortKey="dateDerniereAction"
                                sort={sort}
                                onSort={handleSort}
                                className="min-w-[180px] max-w-[220px]"
                            />

                            {/* 9. Prochaine action */}
                            <SortHeader
                                label="Prochaine action"
                                sortKey="prochaineAction"
                                sort={sort}
                                onSort={handleSort}
                                className="min-w-[180px] max-w-[220px]"
                            />

                            {/* 10. Date prochaine action */}
                            <SortHeader
                                label="Date prochaine action"
                                sortKey="dateProchaineAction"
                                sort={sort}
                                onSort={handleSort}
                                className="min-w-[140px]"
                            />

                            {/* 11. Responsable */}
                            <SortHeader
                                label="Responsable"
                                sortKey="responsable"
                                sort={sort}
                                onSort={handleSort}
                                className="min-w-[110px]"
                            />

                            {/* 12. Notes & Frein */}
                            <Th className="min-w-[240px] max-w-[340px]">Notes & Frein</Th>

                            {/* 13. Actions (Sticky Right) */}
                            <Th className="sticky right-0 z-30 min-w-[160px] pr-8 pl-4 text-right shadow-[-3px_0_6px_-2px_rgba(0,0,0,0.06)] border-l border-slate-200">
                                Actions
                            </Th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan="13" className="px-8 py-16 text-center">
                                    <Loader2 size={20} className="animate-spin text-slate-400 mx-auto" />
                                </td>
                            </tr>
                        )}

                        {!loading && loadError && (
                            <tr>
                                <td colSpan="13" className="px-8 py-16 text-center text-sm font-bold text-red-500">{loadError}</td>
                            </tr>
                        )}

                        {!loading && !loadError && pageRows.map(p => {
                            const isDeleting = deletingId === p._id;
                            const echeance = echeanceState(p);
                            const wa = whatsappLink(p.telephone);
                            return (
                                <tr
                                    key={p._id}
                                    onClick={() => !isDeleting && setDrawer({ mode: 'history', id: p._id })}
                                    className={`group transition-colors cursor-pointer align-middle ${
                                        isDeleting ? 'opacity-40 pointer-events-none' : 'hover:bg-slate-50/80'
                                    } ${echeance === 'late' ? 'bg-red-50/20' : ''}`}
                                >
                                    {/* 1. Établissement (Sticky Left avec Checkbox) */}
                                    <td className="sticky left-0 bg-white group-hover:bg-slate-50 z-10 min-w-[250px] max-w-[280px] pl-5 pr-4 py-3.5 border-b border-slate-100 border-r border-slate-200 shadow-[3px_0_6px_-2px_rgba(0,0,0,0.06)]">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(p._id)}
                                                onChange={(e) => { e.stopPropagation(); toggleSelect(p._id); }}
                                                className="w-4 h-4 rounded text-emerald-600 border-slate-300 focus:ring-emerald-500 cursor-pointer shrink-0"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-bold text-slate-900 tracking-tight truncate" title={p.etablissement}>
                                                    {p.etablissement}
                                                </div>
                                                {(p.typeEtablissement || p.ville) && (
                                                    <div className="text-[10px] font-mono text-slate-400 uppercase mt-0.5 truncate" title={[p.typeEtablissement, p.ville].filter(Boolean).join(' · ')}>
                                                        {[p.typeEtablissement, p.ville].filter(Boolean).join(' · ')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* 2. Contact */}
                                    <td className="px-4 py-3.5 text-xs font-semibold text-slate-700 whitespace-nowrap min-w-[140px] max-w-[160px] truncate border-b border-slate-100" title={p.contact}>
                                        {p.contact || <span className="text-slate-300 font-normal">—</span>}
                                    </td>

                                    {/* 3. Téléphone */}
                                    <td className="px-4 py-3.5 whitespace-nowrap min-w-[140px] border-b border-slate-100" onClick={e => e.stopPropagation()}>
                                        {p.telephone ? (
                                            <div className="flex items-center gap-1.5">
                                                <a href={`tel:${p.telephone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1 text-xs font-mono font-bold text-slate-700 hover:text-blue-600 transition">
                                                    <Phone size={11} className="text-slate-400" />
                                                    {p.telephone}
                                                </a>
                                                {wa && (
                                                    <a href={wa} target="_blank" rel="noreferrer" title="Ouvrir WhatsApp" className="p-1 rounded text-emerald-500 hover:bg-emerald-50 transition">
                                                        <MessageCircle size={12} />
                                                    </a>
                                                )}
                                            </div>
                                        ) : <span className="text-slate-300 text-xs">—</span>}
                                    </td>

                                    {/* 4. Email */}
                                    <td className="px-4 py-3.5 min-w-[180px] max-w-[200px] border-b border-slate-100" onClick={e => e.stopPropagation()}>
                                        {p.email ? (
                                            <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-blue-600 truncate max-w-full transition" title={p.email}>
                                                <Mail size={11} className="text-slate-400 shrink-0" />
                                                <span className="truncate">{p.email}</span>
                                            </a>
                                        ) : <span className="text-slate-300 text-xs">—</span>}
                                    </td>

                                    {/* 5. Source */}
                                    <td className="px-4 py-3.5 min-w-[110px] border-b border-slate-100">
                                        <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded whitespace-nowrap">
                                            {SOURCE_MAP[p.source]?.label || '—'}
                                        </span>
                                    </td>

                                    {/* 6. Statut (Inline selectable) */}
                                    <td className="px-4 py-3.5 min-w-[175px] border-b border-slate-100" onClick={e => e.stopPropagation()}>
                                        <div className="relative inline-flex items-center">
                                            <StatutBadge statut={p.statut} />
                                            {updatingStatutId === p._id && <Loader2 size={12} className="animate-spin text-slate-400 ml-1.5" />}
                                            <select
                                                value={p.statut}
                                                onChange={e => handleInlineStatut(p, e.target.value)}
                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                                title="Cliquer pour changer le statut"
                                            >
                                                {STATUTS.map(s => <option key={s.value} value={s.value}>{s.emoji} {s.label}</option>)}
                                            </select>
                                        </div>
                                    </td>

                                    {/* 7. Intérêt */}
                                    <td className="px-4 py-3.5 min-w-[140px] border-b border-slate-100">
                                        <InteretBar value={p.interet || 0} />
                                    </td>

                                    {/* 8. Dernière action */}
                                    <td className="px-4 py-3.5 min-w-[180px] max-w-[220px] border-b border-slate-100">
                                        <div className="text-xs font-semibold text-slate-800 truncate" title={p.derniereAction}>
                                            {p.derniereAction || <span className="text-slate-300 font-normal">—</span>}
                                        </div>
                                        {p.dateDerniereAction && (
                                            <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                                                {formatDate(p.dateDerniereAction)}
                                            </div>
                                        )}
                                    </td>

                                    {/* 9. Prochaine action */}
                                    <td className="px-4 py-3.5 min-w-[180px] max-w-[220px] border-b border-slate-100">
                                        <ProchaineActionBadge prospect={p} />
                                    </td>

                                    {/* 10. Date prochaine action */}
                                    <td className="px-4 py-3.5 min-w-[140px] whitespace-nowrap border-b border-slate-100">
                                        <EcheanceBadge prospect={p} />
                                    </td>

                                    {/* 11. Responsable */}
                                    <td className="px-4 py-3.5 text-xs font-semibold text-slate-700 whitespace-nowrap min-w-[110px] border-b border-slate-100">
                                        {p.responsable || <span className="text-slate-300 font-normal">—</span>}
                                    </td>

                                    {/* 12. Notes & Frein */}
                                    <td className="px-4 py-3.5 min-w-[240px] max-w-[340px] border-b border-slate-100">
                                        {p.objection && (
                                            <div className="mb-1">
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-red-700 bg-red-50 border border-red-200" title={`Frein : ${p.objection}`}>
                                                    <span>🛑</span>
                                                    <span className="truncate max-w-[220px]">{p.objection}</span>
                                                </span>
                                            </div>
                                        )}
                                        <div className="text-xs text-slate-500 line-clamp-2 leading-relaxed" title={p.notes}>
                                            {p.notes || (!p.objection && <span className="text-slate-300 font-normal">—</span>)}
                                        </div>
                                    </td>

                                    {/* 13. Actions (Sticky Right) */}
                                    <td className="sticky right-0 bg-white group-hover:bg-slate-50 z-10 min-w-[160px] pr-8 pl-4 py-3.5 text-right border-b border-slate-100 border-l border-slate-200 shadow-[-3px_0_6px_-2px_rgba(0,0,0,0.06)]" onClick={e => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1.5 relative">
                                            {deleteConfirmId === p._id && (
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.95, x: 10 }}
                                                    animate={{ opacity: 1, scale: 1, x: 0 }}
                                                    className="absolute right-28 top-0 z-30 bg-white border border-red-100 shadow-[0_0_40px_-10px_rgba(0,0,0,0.25)] rounded-xl p-3 flex items-center gap-2 whitespace-nowrap"
                                                >
                                                    <span className="text-[11px] font-bold text-slate-600">Supprimer ce prospect ?</span>
                                                    <button onClick={() => setDeleteConfirmId(null)} className="px-2.5 py-1 text-[10px] font-bold text-slate-500 hover:bg-slate-100 rounded-md">Non</button>
                                                    <button onClick={() => handleDelete(p._id)} className="px-2.5 py-1 text-[10px] font-bold text-white bg-red-500 hover:bg-red-600 rounded-md">Oui</button>
                                                </motion.div>
                                            )}
                                            <button
                                                onClick={() => setDrawer({ mode: 'action', id: p._id })}
                                                title="Nouvelle action"
                                                className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-amber-200 text-amber-500 hover:bg-amber-500 hover:border-amber-500 hover:text-white transition"
                                            >
                                                <Zap size={12} />
                                            </button>
                                            <button
                                                onClick={() => setDrawer({ mode: 'history', id: p._id })}
                                                title="Historique des actions"
                                                className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-blue-200 text-blue-600 hover:bg-blue-600 hover:border-blue-600 hover:text-white transition relative"
                                            >
                                                <History size={12} />
                                                {p.historique?.length > 0 && (
                                                    <span className="absolute -top-1 -right-1 px-1 min-w-[14px] h-[14px] flex items-center justify-center bg-blue-600 text-white rounded-full text-[8px] font-mono font-bold leading-none">
                                                        {p.historique.length}
                                                    </span>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setDrawer({ mode: 'edit', id: p._id })}
                                                title="Modifier la fiche"
                                                className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-200 text-slate-400 hover:bg-slate-900 hover:border-slate-900 hover:text-white transition"
                                            >
                                                <Pencil size={12} />
                                            </button>
                                            <button
                                                onClick={() => setDeleteConfirmId(deleteConfirmId === p._id ? null : p._id)}
                                                title="Supprimer"
                                                className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-red-200 text-red-400 hover:bg-red-600 hover:border-red-600 hover:text-white transition"
                                            >
                                                {isDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}

                        {!loading && !loadError && filtered.length === 0 && (
                            <tr>
                                <td colSpan="13" className="px-8 py-20 text-center">
                                    {prospects.length === 0 ? (
                                        <div className="max-w-sm mx-auto">
                                            <Target size={36} className="mx-auto text-slate-300 mb-4" />
                                            <div className="text-sm font-black text-slate-900 mb-1">Aucun prospect pour l'instant</div>
                                            <p className="text-xs text-slate-500 mb-5">Ajoutez votre première école ou centre pour commencer le suivi.</p>
                                            <button
                                                onClick={() => setDrawer({ mode: 'create' })}
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800"
                                            >
                                                <Plus size={14} /> Nouveau Prospect
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-sm font-medium text-slate-400">Aucun prospect ne correspond à ces filtres.</span>
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── Pagination ── */}
            <div className="border-t border-slate-200 bg-white px-8 py-4 flex items-center justify-between shrink-0 sticky bottom-0">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Affichage {filtered.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1} à {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} sur {filtered.length}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft size={14} />
                    </button>
                    <div className="text-xs font-bold text-slate-700 px-2 font-mono">{currentPage} / {totalPages}</div>
                    <button
                        onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ChevronRight size={14} />
                    </button>
                </div>
            </div>

            {/* ── Drawer ── */}
            <Drawer
                isOpen={Boolean(drawer) && (drawer.mode === 'create' || Boolean(drawerProspect))}
                onClose={() => setDrawer(null)}
                title={drawer?.mode === 'create' ? 'Nouveau prospect' : drawerProspect?.etablissement || ''}
            >
                {drawer && drawer.mode !== 'create' && drawerProspect && (
                    <div className="flex items-center bg-slate-100 rounded-lg p-0.5 mb-6 w-fit">
                        <button
                            onClick={() => setDrawer({ mode: 'history', id: drawerProspect._id })}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-md transition ${drawer.mode === 'history' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                            <History size={12} /> Historique ({drawerProspect.historique?.length || 0})
                        </button>
                        <button
                            onClick={() => setDrawer({ mode: 'action', id: drawerProspect._id })}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-md transition ${drawer.mode === 'action' || drawer.mode === 'activity' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                            <Zap size={12} /> Nouvelle action
                        </button>
                        <button
                            onClick={() => setDrawer({ mode: 'edit', id: drawerProspect._id })}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-md transition ${drawer.mode === 'edit' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                            <Pencil size={12} /> Fiche prospect
                        </button>
                    </div>
                )}

                {drawer?.mode === 'create' && (
                    <ProspectForm
                        responsables={responsables}
                        onCancel={() => setDrawer(null)}
                        onSaved={(saved) => {
                            upsertLocal(saved);
                            showToast('Prospect créé');
                            setDrawer({ mode: 'history', id: saved._id });
                        }}
                    />
                )}

                {drawer?.mode === 'edit' && drawerProspect && (
                    <ProspectForm
                        key={`${drawerProspect._id}-${drawerProspect.updatedAt}`}
                        prospect={drawerProspect}
                        responsables={responsables}
                        onCancel={() => setDrawer(null)}
                        onSaved={(saved) => {
                            upsertLocal(saved);
                            showToast('Prospect mis à jour');
                            setDrawer(null);
                        }}
                    />
                )}

                {(drawer?.mode === 'action' || drawer?.mode === 'activity') && drawerProspect && (
                    <ProspectActivity
                        key={drawerProspect._id}
                        prospect={drawerProspect}
                        responsables={responsables}
                        onViewHistory={() => setDrawer({ mode: 'history', id: drawerProspect._id })}
                        onUpdated={(updated, message) => {
                            upsertLocal(updated);
                            if (message) showToast(message);
                            setDrawer({ mode: 'history', id: updated._id });
                        }}
                    />
                )}

                {drawer?.mode === 'history' && drawerProspect && (
                    <ProspectHistory
                        key={drawerProspect._id}
                        prospect={drawerProspect}
                        onUpdated={(updated, message) => {
                            upsertLocal(updated);
                            if (message) showToast(message);
                        }}
                        onNewAction={() => setDrawer({ mode: 'action', id: drawerProspect._id })}
                    />
                )}
            </Drawer>

            {/* ── Modal Import Excel ── */}
            <ProspectImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onImportSuccess={(res) => {
                    fetchData();
                    showToast(res.message || `${res.count} prospect(s) importé(s) avec succès !`);
                }}
            />

            {/* ── Modal Export Excel avec Choix du Périmètre ── */}
            <ProspectExportModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                allProspects={prospects}
                filteredProspects={filtered}
                selectedIds={selectedIds}
                onExport={(targetList, fileName) => {
                    exportProspectsToExcel(targetList, fileName);
                    showToast(`${targetList.length} prospect(s) exporté(s) avec succès !`);
                }}
            />

            {/* ── Toast ── */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        key={toast.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className={`fixed bottom-20 right-8 z-[200] px-4 py-2.5 rounded-lg shadow-lg text-xs font-bold ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'}`}
                    >
                        {toast.message}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Prospects;
